import {
  createCredential,
  postJson,
  patchJson,
  deleteReq,
  isCapacitorNative,
  passkeysSupported
} from '../global/webauthnUtils'

export default function PasskeysManager({ routes, csrf, messages = {} }) {
  const defaults = {
    failedToLoad: 'Failed to load passkeys',
    failedToGetCreationOptions: 'Failed to get creation options',
    registrationFailed: 'Registration failed',
    registrationCancelledOrFailed: 'Passkey registration was cancelled or failed.',
    renameFailed: 'Rename failed',
    removeConfirm: 'Remove this passkey? You will no longer be able to sign in with it.',
    removeFailed: 'Remove failed'
  }
  const msgs = { ...defaults, ...messages }

  function dateString() {
    return new Date().toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  function detectPlatformFromUserAgent(ua) {
    const s = (ua || '').toLowerCase()

    if (s.includes('iphone') || s.includes('ipad') || s.includes('ipod')) return 'iOS'
    if (s.includes('android')) return 'Android'
    if (s.includes('mac os') || s.includes('macintosh')) return 'macOS'
    if (s.includes('windows')) return 'Windows'
    if (s.includes('linux')) return 'Linux'

    return 'Unknown Platform'
  }

  function detectBrowserFromUserAgent(ua) {
    const s = (ua || '').toLowerCase()

    if (s.includes('edg/')) return 'Edge'
    if (s.includes('opr/') || s.includes('opera')) return 'Opera'
    if (s.includes('firefox/')) return 'Firefox'
    if (s.includes('vivaldi/')) return 'Vivaldi'
    if (s.includes('chrome/') || s.includes('crios/')) return 'Chrome'
    if (s.includes('safari/')) return 'Safari'

    return 'Unknown Browser'
  }

  function buildWebPasskeyAlias() {
    const uaData = navigator.userAgentData
    const platform = uaData?.platform || detectPlatformFromUserAgent(navigator.userAgent)

    // `brands` may contain e.g. Chromium/Google Chrome/Microsoft Edge; pick a sensible label.
    const brand = uaData?.brands?.find(b => {
      if (!b?.brand) {
        return false
      }

      return !['Not(A:Brand', 'Not A(Brand)'].includes(b.brand)
    })?.brand

    const browser = brand || detectBrowserFromUserAgent(navigator.userAgent)

    return `${platform} · ${browser} · ${dateString()}`
  }

  function formatCapacitorPlatform(platform) {
    switch (platform) {
      case 'web': return 'Web'
      case 'electron': return 'Electron'
      case 'android': return 'Android'
      case 'ios': return 'iOS'
      default: return platform ?? 'Unknown Platform'
    }
  }

  async function buildCapacitorPasskeyAlias() {
    let deviceName = ''

    try {
      // Optional dependency: only used if present.
      const { Device } = await import('@capacitor/device')
      const nameResult = await Device.getName()
      deviceName = (nameResult?.name || '').trim()
    } catch (e) {
      // ignore (dependency not installed / not available)
    }

    const platform = formatCapacitorPlatform(window.Capacitor?.getPlatform?.())
    const label = deviceName || platform

    return `Membergy-App · ${label} · ${dateString()}`
  }

  return {
    routes,
    csrf,
    credentials: [],
    loading: false,
    error: null,
    supported: false,

    async init() {
      this.supported = passkeysSupported()

      await this.refresh()
    },

    async refresh() {
      this.loading = true
      this.error = null
      try {
        const resp = await fetch(this.routes.index, { credentials: 'same-origin' })
        if (!resp.ok) { throw new Error(msgs.failedToLoad) }
        this.credentials = await resp.json()
      } catch (e) {
        console.error(e)
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    async create() {
      try {
        const optionsResp = await postJson(this.routes.attestOptions, {}, this.csrf)
        if (!optionsResp.ok) { throw new Error(msgs.failedToGetCreationOptions) }

        const serverOptions = await optionsResp.json()

        const alias = (isCapacitorNative() && !window.PublicKeyCredential)
          ? await buildCapacitorPasskeyAlias()
          : buildWebPasskeyAlias()

        const body = await createCredential(serverOptions)
        body.alias = alias

        const verifyResp = await postJson(this.routes.attest, body, this.csrf)
        if (!verifyResp.ok) { throw new Error(msgs.registrationFailed) }
        await this.refresh()
      } catch (e) {
        console.error(e)
        alert(msgs.registrationCancelledOrFailed)
      }
    },

    async rename(id, alias) {
      const r = await patchJson(`${this.routes.base}/${encodeURIComponent(id)}`, { alias }, this.csrf)
      if (!r.ok) { alert(msgs.renameFailed) } else { await this.refresh() }
    },

    async remove(id) {
      if (!confirm(msgs.removeConfirm)) { return }
      const r = await deleteReq(`${this.routes.base}/${encodeURIComponent(id)}`, this.csrf)
      if (!r.ok) { alert(msgs.removeFailed) } else { await this.refresh() }
    }
  }
}
